import { AllEnterpriseModule, ModuleRegistry } from 'ag-grid-enterprise';
import React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useSession } from '../contexts';
import type { ProjectLogEntry } from '../services/timeEntryService';
import { timeEntryService } from '../services/timeEntryService';
import { colors } from '../theme/colors';
import { format } from 'date-fns';
import {
  ActionCellRenderer,
  createColumnDefinitions,
  GridWrapper,
  useGridHandlers,
} from './ag-grid';
import { ProjectHoursDialog } from './ag-grid/ProjectHoursDialog';

ModuleRegistry.registerModules([AllEnterpriseModule]);

interface ProjectLoggedHoursProps {
  selectedDate: Date;
  entries: ProjectLogEntry[];
  onChange: (entries: ProjectLogEntry[]) => void;
  onEntrySaved?: () => void;
  canSubmit: boolean;
  dataLoading?: boolean;
  employeeLoggedHours?: number;
  actualHours?: number;
  plannedHours?: number;
  disabled?: boolean;
  isAbsenceOrVacation?: boolean;
}

export const ProjectLoggedHours: React.FC<ProjectLoggedHoursProps> = ({
  selectedDate,
  entries,
  onChange,
  onEntrySaved,
  dataLoading = false,
  employeeLoggedHours,
  actualHours,
  plannedHours,
  disabled = false,
  isAbsenceOrVacation = false,
}) => {
  const { employeeId } = useSession();
  const [isOpen, setIsOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  // Calculate total project logged hours
  const totalProjectLoggedHours = useMemo(() => {
    return entries.reduce((acc, entry) => acc + entry.hours_logged, 0);
  }, [entries]);

  // Check if project hours match employee hours
  const isMatching = useMemo(() => {
    if (employeeLoggedHours === undefined) return null;
    // Compare with small tolerance for floating point precision
    return Math.abs(totalProjectLoggedHours - employeeLoggedHours) < 0.01;
  }, [totalProjectLoggedHours, employeeLoggedHours]);

  const readOnly = disabled || isAbsenceOrVacation;

  const {
    isLoading,
    setIsLoading,
    deleteRow: baseDeleteRow,
    editRow,
    handleCellValueChanged,
    handleGridReady,
    handleFirstDataRendered,
    handleRowDataUpdated,
    handleExportExcel,
  } = useGridHandlers({ entries, onChange });

  const deleteRow = useCallback(
    (id: string) => {
      baseDeleteRow(id);
      if (onEntrySaved) {
        onEntrySaved();
      }
    },
    [baseDeleteRow, onEntrySaved]
  );

  const columnDefs = useMemo(
    () => createColumnDefinitions(ActionCellRenderer, selectedDate, editRow, deleteRow, readOnly, actualHours, plannedHours, entries),
    [selectedDate, editRow, deleteRow, readOnly, actualHours, plannedHours, entries]
  );

  const handleEmpty = useCallback(async () => {
    if (disabled || isAbsenceOrVacation || isClearing || !employeeId) return;
    if (!window.confirm('Are you sure you want to clear all entries?')) {
      return;
    }

    setIsClearing(true);
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      await timeEntryService.clearAllProjectLoggedHours(employeeId, dateStr);
      
      onChange([]);
      
      if (onEntrySaved) {
        onEntrySaved();
      }
    } catch (error) {
      alert('Failed to clear all entries. Please try again.');
      console.error('Error clearing entries:', error);
    } finally {
      setIsClearing(false);
    }
  }, [employeeId, selectedDate, onChange, disabled, isAbsenceOrVacation, isClearing, onEntrySaved]);

  const handleAddNewRow = useCallback(() => {
    if (disabled || isAbsenceOrVacation) return;
    setIsOpen(true);
  }, [disabled, isAbsenceOrVacation]);

  const handleSaveNewEntry = useCallback(
    (entry: ProjectLogEntry) => {
      onChange([...entries, entry]);
      setIsOpen(false);
      // Trigger refetch after successful save
      if (onEntrySaved) {
        onEntrySaved();
      }
    },
    [entries, onChange, onEntrySaved]
  );

  const handleCloseAddDialog = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <div
      style={{
        backgroundColor: colors.neutral.white,
        borderRadius: '8px',
        marginBottom: '2rem',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
      }}
    >
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Add New Entry Dialog */}
      <ProjectHoursDialog
        selectedDate={selectedDate}
        isOpen={isOpen}
        onClose={handleCloseAddDialog}
        onSave={handleSaveNewEntry}
        onEntrySaved={onEntrySaved}
        actualHours={actualHours}
        plannedHours={plannedHours}
        isEdit={false}
        projectLoggedHours={entries}
      />
      {/* Header */}
      <div
        title="Project Logged Hours"
        style={{
          backgroundColor: colors.primary.background,
          color: colors.primary.text,
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: '1.25rem',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          Project Logged Hours
          {isAbsenceOrVacation && (
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: '500',
                opacity: 0.9,
              }}
            >
              (Read only)
            </span>
          )}
        </h3>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          {employeeLoggedHours !== undefined && isMatching !== null && (
            <div
              style={{
                padding: '0.375rem 0.75rem',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: '600',
                backgroundColor: isMatching ? colors.accent.green : colors.accent.red,
                color: colors.neutral.white,
                display: 'inline-flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              }}
              title={`Project Hours: ${totalProjectLoggedHours.toFixed(2)}h | Employee Hours: ${employeeLoggedHours.toFixed(2)}h`}
            >
              {isMatching ? 'Matching' : 'Not Matching'}
            </div>
          )}
          {!isAbsenceOrVacation && (
            <>
              <button
                onClick={handleAddNewRow}
                disabled={disabled}
                style={{
                  backgroundColor: disabled ? colors.neutral.grey[400] : colors.accent.blue,
                  color: colors.neutral.white,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: disabled ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!disabled) {
                    e.currentTarget.style.backgroundColor = colors.accent.blue + 'dd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!disabled) {
                    e.currentTarget.style.backgroundColor = colors.accent.blue;
                  }
                }}
                title="Add new row (Ctrl+N)"
              >
                + Add Row
              </button>
              <button
                onClick={handleEmpty}
                disabled={disabled || isClearing}
                style={{
                  backgroundColor: disabled || isClearing ? colors.neutral.grey[400] : colors.neutral.grey[300],
                  color: colors.secondary.text,
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: disabled || isClearing ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: disabled || isClearing ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!disabled && !isClearing) {
                    e.currentTarget.style.backgroundColor = colors.neutral.grey[400];
                  }
                }}
                onMouseLeave={(e) => {
                  if (!disabled && !isClearing) {
                    e.currentTarget.style.backgroundColor = colors.neutral.grey[300];
                  }
                }}
                title={isClearing ? 'Clearing entries...' : 'Clear all entries'}
              >
                {isClearing ? 'Clearing...' : 'Clear All'}
              </button>
            </>
          )}
          {/* <button
            onClick={handleExportExcel}
            style={{
              backgroundColor: colors.accent.green,
              color: colors.neutral.white,
              border: 'none',
              borderRadius: '6px',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.accent.green + 'dd';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = colors.accent.green;
            }}
            title="Export to Excel"
          >
            Export Excel
          </button> */}
        </div>
      </div>

      {/* Grid */}
      <GridWrapper
        key={`grid-${entries.length}-${selectedDate.toISOString()}`}
        entries={entries}
        columnDefs={columnDefs}
        onGridReady={handleGridReady}
        onFirstDataRendered={handleFirstDataRendered}
        onRowDataUpdated={handleRowDataUpdated}
        onCellValueChanged={handleCellValueChanged}
        isLoading={isLoading || dataLoading}
      />

      {/* Footer */}
      <div
        style={{
          backgroundColor: colors.secondary.background,
          padding: '1rem 1.5rem',
          borderTop: `1px solid ${colors.neutral.grey[200]}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '2rem',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontSize: '0.875rem',
              color: colors.secondary.text,
            }}
          >
            <strong>Total Project Logged Hours:</strong> {totalProjectLoggedHours.toFixed(2)}
          </div>
          <div
            style={{
              fontSize: '0.875rem',
              color: colors.secondary.text,
            }}
          >
            <strong>Entries:</strong> {entries.length}
          </div>
        </div>
      </div>
    </div>
  );
};
